import React from 'react';
import { Box, Typography } from '@mui/material';
import { DocDialog } from '../common/DocDialog';

interface RolesDocDialogProps {
    open: boolean;
    handleClose: () => void;
}

const RolesDocDialog: React.FC<RolesDocDialogProps> = ({ open, handleClose }) => {
    return (
        <DocDialog open={open} title="Roles Guide" handleClose={handleClose}>
            <Typography variant="h6" gutterBottom>Overview</Typography>
            <Typography variant="body2" paragraph>
                A role is a named permission set that grants access to features across Logistics Plus
                applications. Roles are defined here, then assigned to people elsewhere: on a user's
                <strong> User Roles</strong> tab to grant the role everywhere, or on a user's
                <strong> Organizations</strong> tab to grant it only within a specific organization.
            </Typography>
            <Typography variant="body2" paragraph>
                Roles live in both Auth0 and the identity database. Every create, rename and delete on
                this page is applied to Auth0 first and saved locally only if Auth0 accepted it, so the
                two never drift apart. If Auth0 is unreachable, the operation fails and nothing changes.
            </Typography>

            <Typography variant="h6" gutterBottom>System Roles vs. Custom Roles</Typography>
            <Typography variant="body2" paragraph>
                <strong>System roles</strong> — Admin, SuperAdmin and QlikAdmin — are seeded with the
                platform and drive built-in behavior, so they open read-only: their name and description
                cannot be changed and they offer no Delete option. They are always listed first.
            </Typography>
            <Typography variant="body2" paragraph>
                <strong>Custom roles</strong> are the ones you create and manage here. They are listed
                after the system roles, alphabetically.
            </Typography>

            <Typography variant="h6" gutterBottom>Finding a Role</Typography>
            <Typography variant="body2" paragraph>
                Use <strong>Quick Search</strong> to filter the list. It matches both the role name and
                the description, so searching for a capability such as "billing" will find roles that
                only mention it in their description.
            </Typography>

            <Typography variant="h6" gutterBottom>Creating a Role</Typography>
            <Typography variant="body2" paragraph>
                Click <strong>New Role</strong> and fill in:
            </Typography>
            <Box component="ul" sx={{ pl: 2, mb: 2, '& li': { mb: 0.5 } }}>
                <Typography component="li" variant="body2">
                    <strong>Name</strong> (required, up to 256 characters) — must be unique. If the name is
                    already taken, the error appears directly on the Name field. The reserved system role
                    names cannot be used.
                </Typography>
                <Typography component="li" variant="body2">
                    <strong>Description</strong> (optional, up to 255 characters) — explain what the role
                    grants. This is the only place that context is recorded, and it is shown wherever the
                    role is offered for assignment, so it is worth filling in.
                </Typography>
            </Box>

            <Typography variant="h6" gutterBottom>Editing a Role</Typography>
            <Typography variant="body2" paragraph>
                Click any row to open the role, then edit and save. Renaming a custom role does not
                unassign anyone — everyone who holds the role keeps it under the new name.
            </Typography>

            <Typography variant="h6" gutterBottom>Deleting a Role</Typography>
            <Typography variant="body2" paragraph>
                Open the role, then use the menu in the top-right corner of the dialog and choose
                <strong> Delete</strong>. You will be asked to confirm.
            </Typography>
            <Typography variant="body2" paragraph>
                <strong>A role that is still in use cannot be deleted.</strong> If the role is assigned to
                even one user — either directly or within an organization — the delete is rejected. Remove
                every assignment first, then delete the role.
            </Typography>

            <Typography variant="h6" gutterBottom>Audit History</Typography>
            <Typography variant="body2" paragraph>
                The <strong>Audit</strong> column on each row opens that role's full history: who created,
                renamed or changed it and when, with the before and after values for each change.
            </Typography>

            <Typography variant="h6" gutterBottom>Important Notes</Typography>
            <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <Typography component="li" variant="body2">
                    Only tenant administrators can reach this page and manage roles.
                </Typography>
                <Typography component="li" variant="body2">
                    Roles control access but are not the whole picture — which applications a user can reach
                    is governed separately, by the user's application assignments and their organization's
                    application list.
                </Typography>
                <Typography component="li" variant="body2">
                    Deleting a role is permanent. Recreating a role with the same name produces a new role
                    in Auth0, and previous assignments are not restored.
                </Typography>
                <Typography component="li" variant="body2">
                    Assigning and unassigning roles happens on each user's detail page, or in bulk from the
                    Users page using <strong>Assign Roles</strong> and the <strong>Import Users</strong> wizard.
                </Typography>
            </Box>
        </DocDialog>
    );
};

export default RolesDocDialog;
